import BaseApiService from '../../auditor/service/baseApiService.mjs';

/**
 * The query every Reports read takes: `fy` (omitted = the current fiscal year,
 * as the server reckons it), `quarter` all|Q1..Q4, and `region` all|name.
 * The region is only a request: the server clamps it to the caller's scope,
 * so a regional role sending one changes nothing.
 */
const scopeParams = ({ fy, quarter = 'all', region = 'all' } = {}) => {
  const params = {};
  if (fy) params.fy = fy;
  params.quarter = quarter && quarter !== 'all' ? `Q${String(quarter).replace(/^Q/i, '')}` : 'all';
  params.region = region || 'all';
  return params;
};

/** The filename out of a Content-Disposition header, if the server set one. */
const filenameOf = (header) => {
  if (!header) return null;
  const star = /filename\*=(?:UTF-8'')?([^;]+)/i.exec(header);
  if (star) {
    try { return decodeURIComponent(star[1].trim().replace(/^"|"$/g, '')); } catch { /* fall through */ }
  }
  const plain = /filename="?([^";]+)"?/i.exec(header);
  return plain ? plain[1].trim() : null;
};

/**
 * The Reports module's reads. Downloads of a report's files stay on
 * monthlyReportService - these endpoints list, check, and zip a
 * period's final claims.
 */
class ReportsService extends BaseApiService {
  constructor() {
    super();
    this.baseURL = '/reports';
  }

  /** Finally approved reports the caller may see, newest period first. */
  async getArchive() {
    try {
      const response = await this.api.get(`${this.baseURL}/archive`);
      return response.data?.data?.reports || [];
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to load the report archive');
    }
  }

  /**
   * Every FINAL claim in the period: staff claims in a finally approved
   * report, contractor claims once the auditor's final approval is in. Drafts
   * and anything still moving through approval never appear.
   * @returns {{ fiscalYear, quarter, region, regions: string[], claims: object[] }}
   */
  async getClaims(scope) {
    return this.getScoped('claims', scope, 'Failed to load the claims');
  }

  /** Every funding line in the fiscal year's approved reports, and the PTAEs. */
  async getFunding(scope) {
    // Quarter is narrowed in the browser (FundingTab), so only the year and
    // region go to the server, as the contract has it.
    return this.getScoped('funding', { ...scope, quarter: 'all' }, 'Failed to load the funding report');
  }

  /**
   * The auditor's decisions for the period, in the shape the auditor
   * dashboard's getAuditOutcomes returns, so one Excel builder serves both.
   * Auditor (or active delegate) only; anyone else gets a 403.
   */
  async getAudit(scope) {
    return this.getScoped('audit', scope, 'Failed to load the audit decisions');
  }

  /**
   * Every final claim of the period in one zip. Above the server's cap it
   * answers 413 with a message saying how many matched; that message is the
   * one thrown, with `status` set so the caller can tell it apart.
   * @returns {Promise<{ blob: Blob, filename: string|null }>}
   */
  async downloadClaimsZip(scope) {
    return this.downloadZip('claims/zip', scope, 'Failed to build the claims zip');
  }

  // Monthly reports, independent contractors and coaches, each in its own
  // folder laid out like a report archive.
  async downloadReportsBundle(scope) {
    return this.downloadZip('downloads/zip', scope, 'Failed to build the reports download');
  }

  async downloadZip(path, scope, failure) {
    try {
      const response = await this.api.get(`${this.baseURL}/${path}`, {
        params: scopeParams(scope),
        responseType: 'blob',
      });
      return { blob: response.data, filename: filenameOf(response.headers?.['content-disposition']) };
    } catch (error) {
      // A blob error body has to be read before its message can be shown.
      let message = failure;
      const body = error.response?.data;
      if (body && typeof body.text === 'function') {
        try { message = JSON.parse(await body.text()).message || message; } catch { /* not JSON */ }
      } else if (body?.message) {
        message = body.message;
      }
      const e = new Error(message);
      e.status = error.response?.status;
      throw e;
    }
  }

  async getScoped(path, scope, failure) {
    try {
      const response = await this.api.get(`${this.baseURL}/${path}`, { params: scopeParams(scope) });
      return response.data?.data || null;
    } catch (error) {
      const e = new Error(error.response?.data?.message || failure);
      e.status = error.response?.status;
      throw e;
    }
  }

  /**
   * Whether a report's approval chain verifies and its figures still match
   * the final approval: { chainValid, figuresMatch, lines }.
   */
  async checkArchivedReport(reportId) {
    try {
      const response = await this.api.get(`${this.baseURL}/archive/${reportId}/check`);
      return response.data?.data || null;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to check the report');
    }
  }
}

const reportsService = new ReportsService();
export default reportsService;
